/**
 * Promotions Sidebar - Narrow column for ads, events, news
 * Used in the two-column landing page layout
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Calendar, 
  Newspaper, 
  Star, 
  ArrowRight,
  MapPin,
  Users,
  ExternalLink,
  Sparkles,
  Heart,
  Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Upcoming Events Widget
 */
const UpcomingEventsWidget = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events?limit=3&status=upcoming`);
        setEvents(response.data?.events || response.data || []);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 bg-gradient-to-r from-primary/10 to-primary/5">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Calendar className="w-4 h-4 text-primary" />
          Upcoming Events
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-3">
        {events.length > 0 ? (
          events.slice(0, 3).map((event) => {
            const eventDate = new Date(event.start_date || event.date);
            return (
              <div 
                key={event.id}
                className="flex gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/events/${event.id}`)}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex flex-col items-center justify-center">
                  <span className="text-xs font-bold text-primary">
                    {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                  </span>
                  <span className="text-lg font-bold text-primary leading-none">
                    {eventDate.getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm line-clamp-1">{event.title}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {event.location || 'Online'}
                  </p>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2">No upcoming events</p>
        )}
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full text-xs"
          onClick={() => navigate('/events')}
        >
          View All Events
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Latest News Widget
 */
const LatestNewsWidget = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/posts?limit=3`);
        setPosts(response.data?.posts || response.data || []);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 bg-gradient-to-r from-amber-500/10 to-amber-500/5">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Newspaper className="w-4 h-4 text-amber-600" />
          Club News
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-2">
        {posts.length > 0 ? (
          posts.slice(0, 3).map((post) => (
            <div 
              key={post.id}
              className="p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => navigate(`/club-blog/${post.slug || post.id}`)}
            >
              <p className="font-medium text-sm line-clamp-2">{post.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(post.created_at).toLocaleDateString()}
              </p>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2">No news yet</p>
        )}
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full text-xs"
          onClick={() => navigate('/club-blog')}
        >
          Read More
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Featured Sponsor Widget
 */
const FeaturedSponsorWidget = () => {
  const [sponsor, setSponsor] = useState(null);

  useEffect(() => {
    // Mock sponsor data - replace with API call when ready
    setSponsor({
      name: "PetDNA Labs",
      tagline: "Know Your Pet's Story",
      benefit: "15% off for AADC members",
      logo: null,
      url: "#"
    });
  }, []);

  if (!sponsor) return null;

  return (
    <Card className="overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
            <Star className="w-3 h-3 mr-1" />
            Partner
          </Badge>
        </div>
        <h4 className="font-semibold text-sm">{sponsor.name}</h4>
        <p className="text-xs text-muted-foreground">{sponsor.tagline}</p>
        {sponsor.benefit && (
          <p className="text-xs text-amber-700 font-medium mt-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {sponsor.benefit}
          </p>
        )}
        <Button variant="outline" size="sm" className="w-full mt-3 text-xs h-8">
          Learn More
          <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Quick Stats Widget
 */
const QuickStatsWidget = () => {
  const [stats, setStats] = useState({ members: 0, pets: 0, posts: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/stats/public`);
        const data = response.data || {};
        setStats({
          members: data.users?.total || 0,
          pets: data.pets?.total || 0,
          posts: data.posts?.total || 0
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <Card>
      <CardContent className="p-4">
        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          Community
        </h4>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-primary">{stats.members?.toLocaleString() || '0'}</p>
            <p className="text-xs text-muted-foreground">Members</p>
          </div>
          <div>
            <p className="text-lg font-bold text-amber-600">{stats.pets?.toLocaleString() || '0'}</p>
            <p className="text-xs text-muted-foreground">Pets</p>
          </div>
          <div>
            <p className="text-lg font-bold text-emerald-600">{stats.posts?.toLocaleString() || '0'}</p>
            <p className="text-xs text-muted-foreground">Posts</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Join CTA Widget
 */
const JoinCTAWidget = () => {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
      <CardContent className="p-4 text-center">
        <Heart className="w-8 h-8 mx-auto mb-2 text-amber-300" />
        <h4 className="font-bold mb-1">Join Our Pack!</h4>
        <p className="text-xs opacity-90 mb-3">
          Connect with pet lovers worldwide
        </p>
        <Button 
          variant="secondary" 
          size="sm" 
          className="w-full"
          onClick={() => navigate('/register')}
        >
          Get Started Free
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Main Promotions Sidebar Component
 */
const PromotionsSidebar = ({ className = '' }) => {
  return (
    <aside className={`space-y-4 ${className}`}>
      <JoinCTAWidget />
      <QuickStatsWidget />
      <UpcomingEventsWidget />
      <LatestNewsWidget />
      <FeaturedSponsorWidget />
    </aside>
  );
};

export default PromotionsSidebar;
export { 
  UpcomingEventsWidget, 
  LatestNewsWidget, 
  FeaturedSponsorWidget,
  QuickStatsWidget,
  JoinCTAWidget
};
